<div class="p-6 min-h-screen  bg-[#FAFAFA]">

  <form [formGroup]="form" class="space-y-6">
    <!-- Justification Narrative -->
    <div class="">
      <label class="block text-sm font-medium text-gray-700 mb-2">
        Justification Narrative
      </label>
      <div class="relative">
        <textarea
          formControlName="justificationNarrative"
          rows="5"
          placeholder="Briefly describe the purpose of this disbursement"
          class="w-full px-3 py-2.5 border border-gray-200 rounded-md text-sm text-gray-800 placeholder-gray-400 resize-none focus:outline-none focus:border-[#253a81] focus:ring-1 focus:ring-[#253a81]"
        ></textarea>
        <span class="block text-right text-xs text-gray-400 mt-1">
          (Not more than 500 words)
        </span>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <app-upload-card
        label="Upload CAC Standing Report"
        secondaryLabel="(no older than 30 days)"
        [acceptExtensions]="['.jpg', '.png', '.pdf']"
        (fileChange)="onFileChange('cacStandingReport', $event)"
      />
      <app-upload-card
        label="Upload Source-of-Funds Attestation"
        [acceptExtensions]="['.jpg', '.png', '.pdf']"
        (fileChange)="onFileChange('sourceOfFundsAttestation', $event)"
      />
    </div>

    @for (director of directorsArray.controls; track $index) {
      <div class="border border-gray-200 rounded-lg p-4 space-y-4">
        <p class="text-sm font-medium text-gray-700">Director {{ $index + 1 }}</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <app-input
            [control]="directorControl($index, 'directorName')"
            label="Director Name"
            placeholder="Input Name"
            [labelClass]="labelClass"
            [inputClass]="inputClass"
            [useDefaultError]="false"
          />
          <app-select
            [control]="directorControl($index, 'sanctionResult')"
            label="Sanction Result"
            placeholder="Select Option"
            [options]="sanctionOptions"
            [labelClass]="labelClass"
            [inputClass]="inputClass"
            [useDefaultError]="false"
          />
        </div>
      </div>
    }

    <div class="space-y-4 flex flex-col gap-1">
      <app-upload-card
        label="SEC Registered License Number"
        [acceptExtensions]="['.jpg', '.png', '.pdf']"
        (fileChange)="onFileChange('secRegisteredLicenseNumber', $event)"
      />
      <app-upload-card
        label="Upload SEC Registered License"
        secondaryLabel="(capital-markets registrar only)"
        [acceptExtensions]="['.jpg', '.png', '.pdf']"
        (fileChange)="onFileChange('secRegistrarLicence', $event)"
      />
    </div>

    <section class="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white" aria-labelledby="funding-account-heading">
      <div class="flex items-center justify-between gap-3 bg-[#F5F5F5] px-4 py-3">
        <h3 id="funding-account-heading" class="text-base font-medium text-[#17191C]">Funding Account</h3>
        <app-button
          type="button"
          [inputClass]="'!bg-white !text-[#253a81] !border !border-[#E6E6E6] whitespace-nowrap !text-xs !px-3 !py-2'"
          [loading]="addingFundingAccount()"
          [buttonDisabled]="addingFundingAccount() || accountNameEnquiry.resolving()"
          (buttonClick)="onAddFundingAccount()"
        >
          <span class="flex items-center gap-2">
            Add New Funding Account
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 8H12M8 12V4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
          </span>
        </app-button>
      </div>

      <div class="space-y-4 p-4">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <app-select
            [control]="accountControl('fundingModel')"
            label="Funding model"
            placeholder="Select Funding Model"
            [options]="fundingModelOptions"
            [labelClass]="labelClass"
            [inputClass]="inputClass"
            [useDefaultError]="false"
          />
          <app-input
            [control]="accountControl('accountNumber')"
            label="Funding Account Number"
            placeholder="Input Account Number"
            [labelClass]="labelClass"
            [inputClass]="inputClass"
            errorMessage="Enter a valid 10-digit account number."
            [digitsOnly]="true"
            [maxLength]="10"
          />
          <app-select
            [control]="accountControl('bankName')"
            label="Bank Name"
            [placeholder]="banksLoading() ? 'Loading banks...' : 'Select Bank Name'"
            [options]="banks()"
            displayName="institutionName"
            value="institutionName"
            [labelClass]="labelClass"
            [inputClass]="inputClass"
            [useDefaultError]="false"
            [searchable]="true"
          />

          <div [attr.aria-busy]="accountNameEnquiry.resolving() ? 'true' : null">
            <app-input
              [control]="accountControl('accountName')"
              label="Account Name"
              placeholder="Input Account Name"
              id="activation-account-name"
              describedBy="activation-account-name-status"
              [readonly]="true"
              [labelClass]="labelClass"
              [inputClass]="inputClass"
              [useDefaultError]="false"
              [showSuffix]="true"
            >
              @if (accountNameEnquiry.resolving()) {
                <svg suffix class="animate-spin h-4 w-4 text-[#253a81]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              }
            </app-input>

            <!-- Permanently mounted: a live region only announces changes to content
                 already in the DOM, so the @if goes inside, never around it. -->
            <p id="activation-account-name-status" role="status" aria-live="polite" aria-atomic="true" class="text-sm mt-1">
              @if (accountNameEnquiry.resolving()) {
                <span class="text-gray-500">Confirming account name&hellip;</span>
              } @else if (accountNameEnquiry.failed()) {
                <span class="text-red-600">{{ accountNameEnquiry.errorMessage() }}</span>
              }
            </p>

            @if (accountNameEnquiry.failed()) {
              <button type="button" class="text-sm underline mt-1" (click)="accountNameEnquiry.retry()"
                aria-label="Retry funding account name lookup">Retry</button>
            }
          </div>
        </div>

        <div>
          <label for="funding-amount-threshold" class="block text-sm font-medium text-gray-700 mb-1">Amount Threshold</label>
          <div class="flex items-center border border-gray-200 bg-white rounded-md overflow-hidden">
            <span class="px-3 py-2.5 text-sm text-gray-500" aria-hidden="true">₦</span>
            <input
              id="funding-amount-threshold"
              type="number"
              min="1"
              [formControl]="accountControl('amountThreshold')"
              placeholder="Input Amount"
              aria-describedby="funding-amount-threshold-hint"
              class="flex-1 py-2.5 pr-3 text-sm outline-none bg-white"
            />
          </div>
          <p id="funding-amount-threshold-hint" class="mt-2 flex items-center gap-1 text-xs text-[#1D4FBF]">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.25"/>
              <path d="M8 7v4" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>
              <circle cx="8" cy="4.5" r="0.75" fill="currentColor"/>
            </svg>
            Set the maximum debit amount for this disbursement run
          </p>
          @if (accountControl('amountThreshold').touched && accountControl('amountThreshold').invalid) {
            <p class="text-red-500 text-xs mt-1">Enter an amount greater than zero.</p>
          }
        </div>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label for="funding-start-date" class="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              id="funding-start-date"
              type="date"
              [formControl]="accountControl('startDate')"
              [min]="minDate"
              class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-md outline-none bg-white focus:border-[#253a81] focus:ring-1 focus:ring-[#253a81]"
            />
            @if (accountControl('startDate').touched && accountControl('startDate').invalid) {
              <p class="text-red-500 text-xs mt-1">Start date is required.</p>
            }
          </div>
          <div>
            <label for="funding-end-date" class="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              id="funding-end-date"
              type="date"
              [formControl]="accountControl('endDate')"
              [min]="accountControl('startDate').value || minDate"
              class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-md outline-none bg-white focus:border-[#253a81] focus:ring-1 focus:ring-[#253a81]"
            />
            @if (accountControl('endDate').touched && accountControl('endDate').invalid) {
              <p class="text-red-500 text-xs mt-1">End date is required.</p>
            }
          </div>
        </div>

        <div class="flex items-center gap-2 bg-[#FEF9F0] border border-[#FCEDD2] rounded-lg p-3">
          <svg class="shrink-0" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="6.5" stroke="#F5A022" stroke-width="1.25" />
            <path d="M8 5v3.5" stroke="#F5A022" stroke-width="1.25" stroke-linecap="round" />
            <circle cx="8" cy="10.75" r="0.75" fill="#F5A022" />
          </svg>
          <p class="text-sm text-[#8A6415]">
            Inputting this information means you are giving us permission to debit from this account during the proposed disbursement date
          </p>
        </div>
      </div>
    </section>

    <div class="flex justify-end">
      <div class="flex items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2">
        <span id="default-funding-account-label" class="text-xs text-gray-700">Set as default funding account</span>
        <button
          type="button"
          role="switch"
          aria-labelledby="default-funding-account-label"
          [attr.aria-checked]="accountControl('isDefault').value"
          class="relative h-4 w-7 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#253a81]"
          [class.bg-[#253a81]]="accountControl('isDefault').value"
          [class.bg-gray-300]="!accountControl('isDefault').value"
          (click)="toggleDefaultFundingAccount()"
        >
          <span
            class="absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white transition-transform"
            [class.translate-x-3]="accountControl('isDefault').value"
          ></span>
        </button>
      </div>
    </div>

    @if (savedFundingAccounts().length) {
      <div class="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
        @for (account of savedFundingAccounts(); track account.id; let i = $index) {
          <div class="rounded-lg bg-[#F5F5F5]">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
              [attr.aria-expanded]="account.expanded"
              [attr.aria-controls]="'funding-account-card-' + i"
              (click)="toggleFundingAccountCard(i)"
            >
              <span class="flex items-center gap-2">
                <span class="text-sm font-medium text-[#17191C]">Funding Account {{ i + 1 }} Information</span>
                @if (account.status === 'Rejected') {
                  <span class="rounded-full bg-[#FEF3F2] px-2 py-0.5 text-xs text-[#B42318]">Direct debit failed</span>
                } @else if (account.status === 'Expired') {
                  <span class="rounded-full bg-[#FEF3F2] px-2 py-0.5 text-xs text-[#B42318]">Expired</span>
                }
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" class="shrink-0 transition-transform" [class.rotate-180]="!account.expanded" aria-hidden="true">
                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            @if (account.expanded) {
              <dl [id]="'funding-account-card-' + i" class="mx-2 mb-2 grid grid-cols-2 gap-x-4 gap-y-4 rounded-md bg-white p-4 md:grid-cols-4">
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Funding model</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">Per-run Debit</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Funding Account Number</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ account.accountNumber }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Bank Name</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ account.bankName }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Account Name</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ account.accountName }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Amount Threshold</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ formatAmount(account.amountThreshold) }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Start Date</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ formatDate(account.startDate) }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">End Date</dt>
                  <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ formatDate(account.endDate) }}</dd>
                </div>
                <div>
                  <dt class="text-xs text-[#6B6B6A]">Default Funding Account</dt>
                  <dd class="mt-1">
                    @if (account.isDefault) {
                      <span class="rounded-full bg-[#ECFDF3] px-2.5 py-0.5 text-xs font-medium text-[#067647]">Default</span>
                    } @else {
                      <span class="text-sm font-semibold text-[#17191C]">—</span>
                    }
                  </dd>
                </div>
              </dl>
            }
          </div>
        }
      </div>
    }
    
    <label class="flex items-start gap-3 cursor-pointer select-none">
      <input
        type="checkbox"
        class="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-[#253a81]"
        [checked]="termsAccepted()"
        (change)="termsAccepted.set($any($event.target).checked)"
      />
      <span class="text-sm text-gray-700">
        I acknowledge that I have read and accepted
        <button type="button" class="font-semibold text-[#253a81] hover:opacity-75" (click)="showTermsModal.set(true)">Disbursement's Terms &amp; Condition</button>
        and I agree to proceed with this disbursement
      </span>
    </label>

    <div>
      <app-button
        [loading]="submitting()"
        [buttonDisabled]="submitting() || !termsAccepted() || accountNameEnquiry.resolving()"
        (buttonClick)="onSubmit()"
      >
        Submit
      </app-button>
    </div>
  </form>
</div>

@if (showTermsModal()) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="terms-modal-title">
    <div class="absolute inset-0 bg-black/50" (click)="showTermsModal.set(false)"></div>
    <div class="relative z-10 w-full max-w-lg bg-white rounded-xl shadow-xl flex flex-col max-h-[80vh]">
      <div class="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
        <h2 id="terms-modal-title" class="text-base font-semibold text-gray-900">Disbursement's Terms &amp; Condition</h2>
        <button type="button" class="flex items-center justify-center w-7 h-7 rounded-full border border-gray-300 text-gray-500 hover:bg-gray-50" (click)="showTermsModal.set(false)" aria-label="Close">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M11 3L3 11M3 3l8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        </button>
      </div>
      <div class="overflow-y-auto px-6 py-5 text-sm text-gray-700 leading-relaxed space-y-4">
        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
        <p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.</p>
        <p>Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.</p>
        <p>Nemo enim ipsam voluptatem quia voluptas sit aspernatur aut odit aut fugit, sed quia consequuntur magni dolores eos qui ratione voluptatem sequi nesciunt. Neque porro quisquam est, qui dolorem ipsum quia dolor sit amet.</p>
        <p>At vero eos et accusamus et iusto odio dignissimos ducimus qui blanditiis praesentium voluptatum deleniti atque corrupti quos dolores et quas molestias excepturi sint occaecati cupiditate non provident.</p>
        <p>Similique sunt in culpa qui officia deserunt mollitia animi, id est laborum et dolorum fuga. Et harum quidem rerum facilis est et expedita distinctio. Nam libero tempore, cum soluta nobis est eligendi optio cumque nihil impedit quo minus.</p>
        <p>Temporibus autem quibusdam et aut officiis debitis aut rerum necessitatibus saepe eveniet ut et voluptates repudiandae sint et molestiae non recusandae. Itaque earum rerum hic tenetur a sapiente delectus, ut aut reiciendis voluptatibus maiores.</p>
        <p>Quis autem vel eum iure reprehenderit qui in ea voluptate velit esse quam nihil molestiae consequatur, vel illum qui dolorem eum fugiat quo voluptas nulla pariatur. Lorem ipsum dolor sit amet, consectetur adipiscing elit.</p>
      </div>
      <div class="px-6 py-4 border-t border-gray-100 shrink-0">
        <button type="button" class="w-full py-2.5 rounded-md bg-[#253a81] text-white text-sm font-medium hover:bg-[#1e2f6a]" (click)="showTermsModal.set(false)">Close</button>
      </div>
    </div>
  </div>
}

@if (mandateModal(); as modal) {
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="activation-success-modal-title">
    <div class="absolute inset-0 bg-black/50"></div>
    <div class="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      <div class="flex flex-col items-center text-center">
        <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3FCF7]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 12.5l2 2 4-4.5" stroke="#37C55F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10.29 2.86a2 2 0 013.42 0l.7 1.16c.32.53.9.85 1.52.85h1.35a2 2 0 012 2v1.35c0 .62.32 1.2.85 1.52l1.16.7a2 2 0 010 3.42l-1.16.7c-.53.32-.85.9-.85 1.52v1.35a2 2 0 01-2 2h-1.35c-.62 0-1.2.32-1.52.85l-.7 1.16a2 2 0 01-3.42 0l-.7-1.16a1.78 1.78 0 00-1.52-.85H6.72a2 2 0 01-2-2v-1.35c0-.62-.32-1.2-.85-1.52l-1.16-.7a2 2 0 010-3.42l1.16-.7c.53-.32.85-.9.85-1.52V6.87a2 2 0 012-2h1.35c.62 0 1.2-.32 1.52-.85l.7-1.16z" stroke="#37C55F" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>

        <h2 id="activation-success-modal-title" class="mt-3 text-lg font-semibold text-[#17191C]">Request Submitted Successfully</h2>
        <p class="mt-1 text-sm text-[#6B6B6A]">Your request has been submitted. We'll notify you when it's active.</p>
      </div>

      @if (modal.fundingSource.activationInstructions; as instructions) {
        <div class="mt-5 rounded-xl border border-[#37C55F] bg-[#F3FCF7] p-4">
          <p class="text-sm text-[#17191C]">
            Please send a minimum of <strong>₦{{ instructions.amount }}</strong> to the account details below to activate your direct debit.
          </p>

          <dl class="mt-3 space-y-3 rounded-lg bg-white p-4">
            <div>
              <dt class="text-xs text-[#9A9A9A]">Bank Name</dt>
              <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ instructions.bankName }}</dd>
            </div>
            <div>
              <dt class="text-xs text-[#9A9A9A]">Account Name</dt>
              <dd class="mt-1 text-sm font-semibold text-[#17191C]">{{ instructions.accountName }}</dd>
            </div>
            <div>
              <dt class="text-xs text-[#9A9A9A]">Account Number</dt>
              <dd class="mt-1 flex items-center gap-1.5">
                <span class="text-sm font-semibold text-[#17191C]">{{ instructions.accountNumber }}</span>
                <button
                  type="button"
                  class="flex h-5 w-5 items-center justify-center text-[#6B6B6A] hover:text-[#17191C]"
                  (click)="copyActivationAccountNumber()"
                  aria-label="Copy account number"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                    <rect x="5.5" y="5.5" width="8.5" height="8.5" rx="1.5" stroke="currentColor" stroke-width="1.2"/>
                    <path d="M10.5 5.5V3.5C10.5 2.67157 9.82843 2 9 2H3.5C2.67157 2 2 2.67157 2 3.5V9C2 9.82843 2.67157 10.5 3.5 10.5H5.5" stroke="currentColor" stroke-width="1.2"/>
                  </svg>
                </button>
              </dd>
            </div>
          </dl>
        </div>
      }

      <div class="mt-6 flex items-center justify-end gap-3">
        <button
          type="button"
          class="rounded-md border border-[#E6E6E6] px-5 py-2.5 text-sm font-medium text-[#4D4D4D] hover:bg-gray-50 disabled:opacity-50"
          [disabled]="confirmingActivationFee()"
          (click)="closeMandateModal()"
        >
          Cancel
        </button>
        <app-button
          class="block"
          [inputClass]="'!py-2.5 !px-5 whitespace-nowrap'"
          [loading]="confirmingActivationFee()"
          [buttonDisabled]="confirmingActivationFee()"
          (buttonClick)="onActivationFeeSent()"
        >
          Activation Fee Sent
        </app-button>
      </div>
    </div>
  </div>
}
